import { first, isEmpty, last } from 'lodash';
import { useEffect, useRef } from 'react';
import { getController } from '@shared/util/timeout';

const FOCUSABLE = [
  'button:not([disabled])',
  '[href]',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"]:not([disabled]))',
].join(',');

export function useFocusTrap(enabled: boolean) {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const container = ref.current;
    if (!enabled || !container) {
      return;
    }
    container.dataset.focusTrap = 'true';
    requestAnimationFrame(() => container.focus());

    const onKeyDown = (e: KeyboardEvent) => {
      const containerHasChildTrap = container.querySelector('[data-focus-trap="true"]');
      if (containerHasChildTrap) {
        return;
      }
      const elements = Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (isEmpty(elements)) {
        return;
      }

      switch (e.key) {
        case 'Tab':
          handleTabKey(e, elements);
          break;
        default:
          break;
      }
    };

    const { signal, abort } = getController();
    container.addEventListener('keydown', onKeyDown, { signal });

    return () => {
      container.dataset.focusTrap = 'false';
      abort();
    };
  }, [ref, enabled]);

  return ref;
}

function handleTabKey(e: KeyboardEvent, elements: HTMLElement[]) {
  const firstElement = first(elements);
  const lastElement = last(elements);
  if (e.shiftKey && document.activeElement === firstElement) {
    e.preventDefault();
    lastElement?.focus();
  } else if (!e.shiftKey && document.activeElement === lastElement) {
    e.preventDefault();
    firstElement?.focus();
  }
}
